import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  ArrowCounterClockwise,
  Camera,
  CheckCircle,
  Paperclip,
  QrCode,
  Shield,
  Spinner,
  Warning,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { QrScanner, compressInvoiceFile } from '../components/expense-report';
import {
  createExpenseReport,
  getExpenseReportConfig,
  getExpenseReportError,
  getExpenseReportSuppliers,
  lookupExpenseInvoice,
} from '../lib/api/expenseReportApi';

const STORAGE_KEYS = { reporter: 'valhalla_expense_reporter', accessKey: 'valhalla_expense_key' };
const CREATE_SUPPLIER = '__create__';

const ERROR_MESSAGES = {
  invalid_code: 'Ese código no parece un CUFE válido. Escanea el QR o pega el CUFE completo.',
  invoice_not_found: 'La DGI no tiene una factura con ese CUFE. Si es muy reciente, intenta en unos minutos.',
  dgi_unavailable: 'La DGI no responde ahora mismo. Intenta de nuevo en un momento.',
  duplicate: 'Esta factura ya fue reportada.',
  rate_limited: 'Demasiados intentos. Espera unos minutos.',
  daily_cap: 'Se alcanzó el límite diario de reportes.',
  file_too_large: 'El archivo pesa más de 5 MB.',
  invalid_file: 'La copia debe ser una foto (JPG, PNG, WebP) o un PDF.',
  supplier_required: 'Elige un proveedor.',
  reporter_required: 'Escribe tu nombre.',
  invalid_date: 'La fecha no puede estar en el futuro.',
  network: 'Sin conexión con el servidor. Revisa tu internet.',
};

const money = (value) => `$${Number(value || 0).toFixed(2)}`;

const safeStorage = {
  get: (key) => {
    try { return localStorage.getItem(key) || ''; } catch { return ''; }
  },
  set: (key, value) => {
    try { localStorage.setItem(key, value); } catch { /* storage unavailable */ }
  },
  remove: (key) => {
    try { localStorage.removeItem(key); } catch { /* storage unavailable */ }
  },
};

// Makes the page installable as its own phone app without touching the ERP's manifest.
function useInstallableMeta() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Valhalla · Gastos';
    const added = [];
    const add = (tag, attrs) => {
      const element = document.createElement(tag);
      Object.entries(attrs).forEach(([key, value]) => element.setAttribute(key, value));
      document.head.appendChild(element);
      added.push(element);
    };
    add('link', { rel: 'manifest', href: '/gasto-manifest.json' });
    add('link', { rel: 'apple-touch-icon', href: '/gasto-icon-180.png' });
    add('meta', { name: 'apple-mobile-web-app-capable', content: 'yes' });
    add('meta', { name: 'apple-mobile-web-app-title', content: 'Gastos' });
    add('meta', { name: 'apple-mobile-web-app-status-bar-style', content: 'black-translucent' });
    add('meta', { name: 'theme-color', content: '#0B0E14' });
    return () => {
      document.title = previousTitle;
      added.forEach((element) => element.remove());
    };
  }, []);
}

const fieldClass = 'h-12 border-[#242E42] bg-[#0B0E14] text-base text-[#F3F4F6]';

export function ExpenseReportPage() {
  useInstallableMeta();

  const [step, setStep] = useState('scan'); // scan | form | done
  const [scanning, setScanning] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [lookingUp, setLookingUp] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [keyRequired, setKeyRequired] = useState(false);
  const [accessKey, setAccessKey] = useState(() => safeStorage.get(STORAGE_KEYS.accessKey));

  const [invoice, setInvoice] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [supplierId, setSupplierId] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState('');
  const [notes, setNotes] = useState('');
  const [reporter, setReporter] = useState(() => safeStorage.get(STORAGE_KEYS.reporter));
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const codeRef = useRef('');

  useEffect(() => {
    getExpenseReportConfig()
      .then(({ data }) => setKeyRequired(Boolean(data.key_required)))
      .catch(() => {});
  }, []);

  const showError = useCallback((err) => {
    const { code, message } = getExpenseReportError(err);
    if (code === 'key_required') {
      safeStorage.remove(STORAGE_KEYS.accessKey);
      setKeyRequired(true);
      setError('La clave de acceso es incorrecta o falta.');
      return;
    }
    setError(ERROR_MESSAGES[code] || message || 'Ocurrió un error. Intenta de nuevo.');
  }, []);

  const reset = () => {
    setStep('scan');
    setInvoice(null);
    setFile(null);
    setNotes('');
    setManualCode('');
    setResult(null);
    setError('');
    codeRef.current = '';
  };

  const lookup = useCallback(async (code) => {
    const trimmed = (code || '').trim();
    if (!trimmed) return;
    setError('');
    setLookingUp(true);
    try {
      const [{ data }, suppliersResponse] = await Promise.all([
        lookupExpenseInvoice(trimmed, accessKey),
        getExpenseReportSuppliers(accessKey),
      ]);
      codeRef.current = trimmed;
      setInvoice(data);
      setSuppliers(suppliersResponse.data);
      setSupplierId(data.matched_supplier_id || (data.duplicate_po_number ? '' : CREATE_SUPPLIER));
      setDescription(data.description);
      setDate(data.invoice_date || '');
      setStep('form');
    } catch (err) {
      showError(err);
    } finally {
      setLookingUp(false);
    }
  }, [accessKey, showError]);

  const handleDetected = (text) => {
    setScanning(false);
    setManualCode(text);
    lookup(text);
  };

  const handleFile = async (event) => {
    const picked = event.target.files?.[0];
    event.target.value = '';
    if (!picked) return;
    setFile(await compressInvoiceFile(picked));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!invoice || invoice.duplicate_po_number) return;
    if (reporter.trim().length < 2) {
      setError(ERROR_MESSAGES.reporter_required);
      return;
    }
    if (!supplierId) {
      setError(ERROR_MESSAGES.supplier_required);
      return;
    }

    const form = new FormData();
    form.append('code', codeRef.current);
    form.append('reporter_name', reporter.trim());
    form.append('description', description);
    form.append('date', date);
    form.append('notes', notes);
    if (supplierId === CREATE_SUPPLIER) form.append('create_supplier', 'true');
    else form.append('supplier_id', supplierId);
    if (file) form.append('file', file);

    setError('');
    setSubmitting(true);
    try {
      const { data } = await createExpenseReport(form, accessKey);
      safeStorage.set(STORAGE_KEYS.reporter, reporter.trim());
      setResult(data);
      setStep('done');
      toast.success(`Gasto reportado: ${data.po_number}`);
    } catch (err) {
      showError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const saveAccessKey = (value) => {
    setAccessKey(value);
    safeStorage.set(STORAGE_KEYS.accessKey, value);
  };

  return (
    <div className="min-h-screen bg-[#0B0E14] text-[#F3F4F6]">
      {scanning && <QrScanner onDetected={handleDetected} onClose={() => setScanning(false)} />}

      <div className="mx-auto w-full max-w-md px-4 pb-10 pt-[max(1.25rem,env(safe-area-inset-top))]">
        <header className="mb-6 flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-[#E2B05E] to-[#F0C47A]">
            <Shield weight="fill" className="h-7 w-7 text-[#0B0E14]" />
          </div>
          <div>
            <h1 className="font-['Outfit'] text-xl font-bold leading-tight">Reportar gasto</h1>
            <p className="text-xs uppercase tracking-[0.15em] text-[#9CA3AF]">Valhalla Corporations</p>
          </div>
        </header>

        {keyRequired && !accessKey && (
          <div className="valhalla-card mb-4 space-y-2 p-4">
            <Label htmlFor="access-key">Clave de acceso</Label>
            <Input
              id="access-key"
              type="password"
              className={fieldClass}
              onBlur={(event) => event.target.value && saveAccessKey(event.target.value.trim())}
            />
          </div>
        )}

        {error && (
          <div role="alert" className="mb-4 flex gap-2 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-300">
            <Warning weight="fill" className="mt-0.5 h-5 w-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {step === 'scan' && (
          <div className="space-y-4">
            <Button
              type="button"
              onClick={() => setScanning(true)}
              disabled={lookingUp}
              className="h-16 w-full gap-3 bg-[#E2B05E] text-lg font-semibold text-[#0B0E14] hover:bg-[#F0C47A]"
            >
              <QrCode weight="bold" className="h-7 w-7" />
              Escanear QR de la factura
            </Button>

            <div className="valhalla-card space-y-3 p-4">
              <Label htmlFor="manual-code">O pega el CUFE / link de la factura</Label>
              <Textarea
                id="manual-code"
                rows={3}
                value={manualCode}
                onChange={(event) => setManualCode(event.target.value)}
                placeholder="FE01200000811194-1-498031-…"
                className="border-[#242E42] bg-[#0B0E14] text-base text-[#F3F4F6]"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => lookup(manualCode)}
                disabled={lookingUp || !manualCode.trim()}
                className="h-12 w-full border-[#242E42] text-base"
              >
                {lookingUp ? <Spinner className="mr-2 h-5 w-5 animate-spin" /> : null}
                {lookingUp ? 'Consultando la DGI…' : 'Buscar factura'}
              </Button>
            </div>
            {lookingUp && (
              <p className="text-center text-sm text-[#9CA3AF]">Consultando la DGI, un momento…</p>
            )}
          </div>
        )}

        {step === 'form' && invoice && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="valhalla-card space-y-1 p-4">
              <p className="text-xs uppercase tracking-wide text-[#9CA3AF]">Factura verificada en la DGI</p>
              <p className="font-['Outfit'] text-lg font-semibold">{invoice.issuer.name}</p>
              <p className="text-sm text-[#9CA3AF]">
                RUC {invoice.issuer.ruc}{invoice.issuer.dv ? ` DV ${invoice.issuer.dv}` : ''}
                {invoice.invoice_number ? ` · Factura ${invoice.invoice_number}` : ''}
              </p>
            </div>

            {invoice.duplicate_po_number && (
              <div role="alert" className="flex gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-200">
                <Warning weight="fill" className="mt-0.5 h-5 w-5 flex-shrink-0" />
                <span>Esta factura ya fue reportada como <strong>{invoice.duplicate_po_number}</strong>. No se puede reportar dos veces.</span>
              </div>
            )}

            {invoice.buyer_check === 'missing' && (
              <div role="alert" className="flex gap-2 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">
                <Warning weight="fill" className="mt-0.5 h-5 w-5 flex-shrink-0" />
                <span>
                  Esta factura no está a nombre de la empresa{invoice.receiver_name ? ` (receptor: ${invoice.receiver_name})` : ''}. Puedes reportarla como gasto,
                  pero <strong>no dará crédito fiscal de ITBMS</strong>. La próxima vez pide la factura con el nombre y RUC de la empresa.
                </span>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="supplier">Proveedor</Label>
              <select
                id="supplier"
                value={supplierId}
                onChange={(event) => setSupplierId(event.target.value)}
                className={`${fieldClass} w-full rounded-md border px-3`}
              >
                <option value="">Selecciona un proveedor…</option>
                <option value={CREATE_SUPPLIER}>➕ Crear «{invoice.issuer.name}» con los datos de la factura</option>
                {suppliers.map((supplier) => (
                  <option key={supplier._id} value={supplier._id}>{supplier.name}</option>
                ))}
              </select>
              {invoice.matched_supplier_id && (
                <p className="text-xs text-emerald-400">Proveedor reconocido por su RUC.</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descripción</Label>
              <Textarea
                id="description"
                rows={3}
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                className="border-[#242E42] bg-[#0B0E14] text-base text-[#F3F4F6]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="date">Fecha</Label>
              <Input id="date" type="date" value={date} onChange={(event) => setDate(event.target.value)} className={fieldClass} required />
            </div>

            <div className="valhalla-card divide-y divide-[#242E42] p-0">
              <div className="flex justify-between p-4 text-[#9CA3AF]"><span>Subtotal</span><span>{money(invoice.subtotal)}</span></div>
              <div className="flex justify-between p-4 text-[#9CA3AF]"><span>ITBMS</span><span>{money(invoice.itbms)}</span></div>
              <div className="flex justify-between p-4 font-['Outfit'] text-xl font-semibold"><span>Total</span><span className="text-[#E2B05E]">{money(invoice.total)}</span></div>
            </div>
            <p className="-mt-2 text-xs text-[#9CA3AF]">Los montos vienen de la DGI y no se pueden editar.</p>

            <div className="space-y-2">
              <Label>Copia de la factura</Label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <input type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFile} />
                  <span className="flex h-12 cursor-pointer items-center justify-center gap-2 rounded-lg border border-[#242E42] text-sm">
                    <Camera className="h-5 w-5" /> Tomar foto
                  </span>
                </label>
                <label className="block">
                  <input type="file" accept="image/*,application/pdf" className="hidden" onChange={handleFile} />
                  <span className="flex h-12 cursor-pointer items-center justify-center gap-2 rounded-lg border border-[#242E42] text-sm">
                    <Paperclip className="h-5 w-5" /> Adjuntar
                  </span>
                </label>
              </div>
              <p className="text-xs text-[#9CA3AF]">
                {file
                  ? `Adjunto: ${file.name} (${Math.max(1, Math.round(file.size / 1024))} KB)`
                  : 'Si no adjuntas nada, se guarda el CAFE oficial de la DGI.'}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notas (opcional)</Label>
              <Textarea id="notes" rows={2} value={notes} onChange={(event) => setNotes(event.target.value)} className="border-[#242E42] bg-[#0B0E14] text-base text-[#F3F4F6]" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="reporter">Quién reporta</Label>
              <Input id="reporter" value={reporter} onChange={(event) => setReporter(event.target.value)} placeholder="Tu nombre" className={fieldClass} required />
            </div>

            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={reset} disabled={submitting} className="h-14 border-[#242E42] px-5">
                <ArrowCounterClockwise className="h-5 w-5" />
                <span className="sr-only">Empezar de nuevo</span>
              </Button>
              <Button
                type="submit"
                disabled={submitting || Boolean(invoice.duplicate_po_number)}
                className="h-14 flex-1 bg-[#E2B05E] text-base font-semibold text-[#0B0E14] hover:bg-[#F0C47A]"
              >
                {submitting ? <Spinner className="mr-2 h-5 w-5 animate-spin" /> : null}
                {submitting ? 'Guardando…' : 'Reportar gasto'}
              </Button>
            </div>
            <p className="text-center text-xs text-[#9CA3AF]">Queda como orden de compra pendiente hasta que se marque como pagada en el ERP.</p>
          </form>
        )}

        {step === 'done' && result && (
          <div className="valhalla-card space-y-4 p-6 text-center">
            <CheckCircle weight="fill" className="mx-auto h-16 w-16 text-emerald-400" />
            <div>
              <p className="font-['Outfit'] text-2xl font-semibold">{result.po_number}</p>
              <p className="text-[#9CA3AF]">{result.supplier_name}</p>
            </div>
            <p className="font-['Outfit'] text-3xl font-bold text-[#E2B05E]">{money(result.total)}</p>
            <p className="text-sm text-[#9CA3AF]">
              {result.has_attachment ? 'Factura adjunta.' : 'Sin copia adjunta.'}
              {result.supplier_created ? ' Se creó el proveedor.' : ''} Pendiente de pago en el ERP.
            </p>
            <Button type="button" onClick={reset} className="h-14 w-full bg-[#E2B05E] text-base font-semibold text-[#0B0E14] hover:bg-[#F0C47A]">
              Reportar otro gasto
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default ExpenseReportPage;
